import { useRef, useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { apiRequest } from "@/lib/queryClient";

// Client-side upload timeout. A stalled /extract-document would otherwise
// animate the in-flight banner forever (no terminal state). This backstop sits
// ABOVE the server's 90s timeout (server/document-analysis.ts) so the server
// reaches its terminal state (clean 504) first and never writes a ghost
// pending_extractions row.
const UPLOAD_TIMEOUT_MS = 100_000;

export interface FileUploadResult {
  fields?: Array<{ fieldId: string; name: string; value: string }>;
  filledCount?: number;
  totalFields?: number;
  confidence?: number;
  rawDescription?: string;
  pendingConfirmation?: boolean;
  pendingId?: string;
  // Async extraction job (session 90): non-preview extract-document now returns
  // a queued jobId instead of synchronous results.
  jobId?: string;
  status?: string;
}

export interface UploadContext {
  sectionId: string;
  fieldId?: string;
  rowId?: string;
}

export interface UseFileUploadOptions {
  clientId: string;
  onSuccess?: (result: FileUploadResult, file: File, ctx: UploadContext) => void;
  onPendingConfirmation?: (result: FileUploadResult, file: File, ctx: UploadContext) => void;
  /** Async extraction enqueued (session 90): the worker will run Gemini in the background. */
  onJobQueued?: (jobId: string, file: File, ctx: UploadContext) => void;
  onError?: (err: Error) => void;
}

export interface UseFileUploadReturn {
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadingKey: string | null;
  /** sectionId of the section currently uploading, or null. Used for per-section loading indicators. */
  activeSectionId: string | null;
  isUploading: boolean;
  /** Open the file dialog, storing context for use when a file is selected. */
  triggerUpload: (sectionId: string, opts?: { fieldId?: string; rowId?: string }) => void;
  /** Attach to the hidden <input type="file"> onChange. */
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export function useFileUpload({
  clientId,
  onSuccess,
  onPendingConfirmation,
  onJobQueued,
  onError,
}: UseFileUploadOptions): UseFileUploadReturn {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const uploadContextRef = useRef<UploadContext | null>(null);

  // D-170.6: converted from a raw apiRequest. Class A — every fire is a billable Gemini
  // extraction, and it runs from a file-input onChange, so there is no button to disable.
  // The abort signal still rides through as a mutation variable, unchanged.
  const uploadFlight = useSingleFlight();
  const extractMutation = useMutation({
    mutationFn: async (v: {
      clientId: string;
      sectionId: string;
      body: Record<string, unknown>;
      signal: AbortSignal;
    }): Promise<FileUploadResult> => {
      const res = await apiRequest(
        "POST",
        `/api/clients/${v.clientId}/sections/${v.sectionId}/extract-document`,
        v.body,
        v.signal,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // Preserve the HTTP status so the consumer can give a server-timeout
        // (504/503) a distinct message from a generic failure.
        const httpErr = new Error(err.message || "Upload failed") as Error & { status?: number };
        httpErr.status = res.status;
        throw httpErr;
      }
      return await res.json();
    },
  });

  const triggerUpload = useCallback(
    (sectionId: string, opts?: { fieldId?: string; rowId?: string }) => {
      uploadContextRef.current = { sectionId, ...opts };
      fileInputRef.current?.click();
    },
    []
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const ctx = uploadContextRef.current;
      if (!file || !ctx) return;
      e.target.value = "";

      const { sectionId, fieldId, rowId } = ctx;
      const key = rowId ? `row:${rowId}` : (fieldId ?? sectionId);
      setUploadingKey(key);
      setActiveSectionId(sectionId);

      // Abort at UPLOAD_TIMEOUT_MS so a stalled request reaches a terminal
      // state → AbortError → onError → the finally below clears the banner.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve((reader.result as string).split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const result = await uploadFlight.run(() =>
          extractMutation.mutateAsync({
            clientId,
            sectionId,
            body: {
              data: base64,
              contentType: file.type,
              filename: file.name,
              fieldId: fieldId || undefined,
              rowId: rowId || undefined,
            },
            signal: controller.signal,
          }),
        );
        // s170: the lock rejected this as a duplicate fire. The `finally` below still clears
        // the banner state this handler set, which is correct — the original fire owns its own.
        if (!result) return;
        if (result.jobId) {
          // Async (session 90): extraction was queued — the caller watches the job.
          onJobQueued?.(result.jobId, file, ctx);
        } else if (result.pendingConfirmation && result.pendingId) {
          onPendingConfirmation?.(result, file, ctx);
        } else {
          onSuccess?.(result, file, ctx);
        }
      } catch (e: any) {
        // A timeout abort surfaces as a DOMException("AbortError"), which is NOT
        // `instanceof Error` — flattening it via new Error(String(e)) would lose
        // the name. Normalize to a named error the onError consumer detects for
        // timeout-distinct copy; pass everything else through (incl. .status).
        if (controller.signal.aborted || e?.name === "AbortError") {
          const timeoutErr = new Error("Upload timed out") as Error & { status?: number };
          timeoutErr.name = "UploadTimeoutError";
          onError?.(timeoutErr);
        } else {
          onError?.(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        clearTimeout(timeoutId);
        setUploadingKey(null);
        setActiveSectionId(null);
        uploadContextRef.current = null;
      }
    },
    [clientId, onSuccess, onPendingConfirmation, onJobQueued, onError]
  );

  return {
    fileInputRef,
    uploadingKey,
    activeSectionId,
    isUploading: uploadingKey !== null,
    triggerUpload,
    handleFileChange,
  };
}
